/**
 * Authentication Store (Zustand)
 *
 * Centralized state management for authentication.
 *
 * State:
 * - isAuthenticated: Whether user is fully logged in (can access app)
 * - user: Current user object with RBAC data (only when fully authenticated)
 * - requires2faSetup: Whether user is in 2FA setup flow (logged in but needs to setup 2FA)
 * - setupUser: Temporary user data during 2FA setup flow
 * - isLoading: Whether auth operation is in progress
 * - isInitialized: Whether initial session validation is complete
 *
 * Persistence:
 * - User and auth state persisted to localStorage via Zustand middleware
 * - Tokens are NOT stored here (they're in HttpOnly cookies)
 *
 * Session Validation:
 * - On app start, call initialize() to validate session with server
 * - This ensures the HttpOnly cookie is still valid
 * - If invalid, user is logged out and redirected
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authService } from '../services/auth'
import { apiService } from '../services/api'
import { AUTH_ENDPOINTS } from '../../api/endpoints'
import { type User } from '../types'
import { useCrisisStore } from './crisisStore'

interface AuthState {
  isAuthenticated: boolean
  user: User | null
  requires2faSetup: boolean
  setupUser: User | null
  isLoading: boolean
  isInitialized: boolean
}

interface AuthActions {
  /**
   * Login with email/password and optional 2FA token.
   */
  login: (email: string, password: string, totpToken?: string) => Promise<User>

  /**
   * Register a new user.
   */
  register: (email: string, password: string, name?: string) => Promise<User>

  /**
   * Logout current user.
   */
  logout: () => Promise<void>

  /**
   * Refresh auth state from localStorage.
   * Used when auth state may have changed externally.
   */
  refreshAuth: () => void

  /**
   * Set loading state.
   */
  setLoading: (loading: boolean) => void

  /**
   * Set current user.
   */
  setUser: (user: User) => void

  /**
   * Set authenticated state.
   */
  setAuthenticated: (isAuthenticated: boolean) => void

  /**
   * Initialize auth state on app start.
   * Validates session with server via /auth/me.
   */
  initialize: () => Promise<void>

  /**
   * Check if user has a specific permission.
   */
  hasPermission: (permission: string) => boolean

  /**
   * Check if user has a specific role by slug.
   */
  hasRole: (roleSlug: string) => boolean

  /**
   * Complete 2FA setup and attempt login.
   */
  complete2faSetup: () => Promise<User>

  setRequires2FASetup: (required: boolean) => void
}

type AuthStore = AuthState & AuthActions

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      isAuthenticated: false,
      user: null,
      requires2faSetup: false,
      setupUser: null,
      isLoading: true,
      isInitialized: false,

      // Actions
      login: async (email: string, password: string, totpToken?: string): Promise<User> => {
        set({ isLoading: true })
        try {
          const result = await authService.login(email, password, totpToken)

          // Check if this is a 2FA setup response
          // authService.login returns the user object with requires2faSetup flag
          console.debug('[AuthStore] Login successful, requires 2fa setup:', result.requires_2faSetup)
          if ((result as User & { requires2faSetup?: boolean }).requires2faSetup) {
            set({
              requires2faSetup: true,
              setupUser: result, // result IS the user object
              isAuthenticated: false,
              user: null,
              isLoading: false,
            })
            throw new Error('auth.twoFactorSetupRequired')
          }



          set({
            isAuthenticated: true,
            user: result,
            requires2faSetup: false,
            setupUser: null,
            isLoading: false,
          })
          return result
        } catch (error) {
          // Don't log "Login failed" for expected 2FA flow
          if (!(error instanceof Error) || !['auth.twoFactorRequired', 'auth.twoFactorSetupRequired'].includes(error.message)) {
            console.error('[AuthStore] Login failed:', error)
          }
          set({ isLoading: false })
          throw error
        }
      },

      register: async (email: string, password: string, name?: string): Promise<User> => {
        set({ isLoading: true })
        try {
          const user = await authService.register(email, password, name)
          set({
            isAuthenticated: true,
            user,
            isLoading: false,
          })
          return user
        } catch (error) {
          console.error('[AuthStore] Registration failed:', error)
          set({ isLoading: false })
          throw error
        }
      },

      logout: async () => {
        console.debug('[AuthStore] Logout initiated')

        // IMPORTANT: Clear all local state FIRST to prevent race conditions
        // This ensures even if the API call fails, the user is logged out locally

        // Reset ALL state including isInitialized
        // This forces a fresh session validation on next page load
        set({
          isAuthenticated: false,
          user: null,
          requires2faSetup: false,
          setupUser: null,
          isLoading: false,
          isInitialized: false, // CRITICAL: Reset so initialize() runs fresh
        })

        // Also reset crisis store
        useCrisisStore.getState().reset()

        // Clear ALL localStorage items related to auth
        localStorage.removeItem('auth_user')
        localStorage.removeItem('auth-storage')
        localStorage.removeItem('crisis-storage')

        // Clear sessionStorage to reset crisis auto-select flag
        sessionStorage.clear()

        // Now call the backend to invalidate server-side tokens
        // This clears HttpOnly cookies which is essential for security
        try {
          await apiService.post(AUTH_ENDPOINTS.LOGOUT, {})
          console.debug('[AuthStore] Backend logout successful')
        } catch (error) {
          // Log but don't throw - local state is already cleared
          // User may need to wait for token expiry if this fails
          console.error('[AuthStore] Backend logout API call failed:', error)
        }

        // Note: No redirect here - let the caller handle it
      },

      refreshAuth: () => {
        const currentAuth = authService.getAuthState()

        set({
          isAuthenticated: currentAuth.isAuthenticated,
          user: currentAuth.user,
          isLoading: false,
        })
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },

      setUser: (user: User) => {
        set({ user })
      },

      setAuthenticated: (isAuthenticated: boolean) => {
        set({ isAuthenticated })
      },

      /**
       * Initialize auth state on app start.
       *
       * Flow:
       * 1. Check if user data exists in localStorage
       * 2. If yes OR if cookies exist, validate session with server via /auth/me
       * 3. If validation succeeds, update user with fresh RBAC data
       * 4. If validation fails (401), clear state and redirect to login
       */
      initialize: async () => {
        const { isInitialized } = get()

        // Only initialize once
        if (isInitialized) {
          set({ isLoading: false })
          return
        }

        set({ isLoading: true })

        // Check for existing user in storage
        // const storedUser = authService.getUser()

        try {
          // Always validate with server - even if no stored user (cookies might exist)
          // This handles the case where user just authenticated via Azure/OAuth
          const validatedUser = await authService.validateSession()

          if (validatedUser) {
            // Session valid - update with fresh user data
            set({
              isAuthenticated: true,
              user: validatedUser,
              isLoading: false,
              isInitialized: true,
            })
          } else {
            // Session invalid - clear state
            authService.clearUser()
            set({
              isAuthenticated: false,
              user: null,
              isLoading: false,
              isInitialized: true,
            })
          }
        } catch (error) {
          console.error('[AuthStore] Session validation failed:', error)

          // On error, assume session is invalid
          // Clear localStorage to ensure user is logged out
          localStorage.removeItem('auth-storage')
          set({
            isAuthenticated: false,
            user: null,
            isLoading: false,
            isInitialized: true,
          })
        }
      },

      /**
       * Check if user has a specific permission.
       * Super admins have all permissions.
       */
      hasPermission: (permission: string): boolean => {
        const { user } = get()
        if (!user) return false
        if (user.isSuperAdmin) return true
        return user.permissions?.includes(permission) ?? false
      },

      /**
       * Check if user has a specific role by slug.
       */
      hasRole: (roleSlug: string): boolean => {
        const { user } = get()
        if (!user) return false
        if (user.isSuperAdmin && (roleSlug === 'super_admin' || roleSlug === 'admin')) return true
        return user.roles?.some(role => role.slug === roleSlug) ?? false
      },

      /**
       * Complete 2FA setup and attempt to log in the user.
       * Called after user completes 2FA setup in the setup flow.
       */
      complete2faSetup: async (): Promise<User> => {
        const { setupUser } = get()
        if (!setupUser) {
          throw new Error('No user in setup mode')
        }

        // Clear setup mode state
        set({
          requires2faSetup: false,
          setupUser: null,
        })

        // 2FA is now enabled, so attempt login again without TOTP
        // The backend will now let the user through since 2FA is enabled
        // Actually, we need to get the credentials from somewhere...
        // For now, just mark as authenticated with the setupUser
        set({
          isAuthenticated: true,
          user: setupUser,
        })

        return setupUser
      },
      setRequires2FASetup: (required: boolean) => {
        set({ requires2faSetup: required })
      }
    }),
    {
      name: 'auth-storage',
      partialize: state => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        // Note: requires2faSetup and setupUser are NOT persisted
        // They are temporary login flow state handled entirely in LoginPage
        // Note: tokens are in HttpOnly cookies, not stored here
        // Note: isInitialized NOT persisted - always re-validate on app load
      }),
    }
  )
)
